import React from 'react';
import moment from 'moment';
import round from './round';
import JavascriptTimeAgo from 'javascript-time-ago';
import en from 'javascript-time-ago/locale/en';
import nl from 'javascript-time-ago/locale/nl';
import ReactTimeAgo from 'react-time-ago';
import { Badge } from '../components/ui-kit/';
import { capitalize } from 'lodash';

const TIME_FORMAT = 'H:mm:ss';
const TIME_ONLY_FORMAT_SHORT = 'H:mm';
const DATETIME_FORMAT = 'DD-MM-YYYY H:mm';
const TIME_FORMAT_SHORT = 'ddd HH:mm';
const HUMANIZED_DATE_TIME_FORMAT = 'ddd DD MMM YYYY HH:mm';
const HUMANIZED_DATE_TIME_FORMAT_SHORT = 'MMM DD, HH:mm';
const DATE_FORMAT = 'DD-MM-YYYY';
const WEEKDAY_FORMAT = 'ddd DD';
export const LOCALE = (globalThis.I18n || {}).locale;
moment.locale(LOCALE);

JavascriptTimeAgo.locale(nl);
JavascriptTimeAgo.locale(en);

export const TimeAgo = ({ timestamp }) => <ReactTimeAgo date={moment(timestamp).toDate()} locale={LOCALE} />;

export const CurrentLabel = () => {
  return <Badge variant="info">{I18n.t('now')}</Badge>;
};

// format a decimal hours value to HH:MM
export const formatContractHours = value => {
  if (value === null || value === undefined) return '';

  let hours = Math.floor(Math.abs(value));
  let minutes = Math.round((Math.abs(value) - hours) * 60, 0);

  // If the minutes round up to a 60, substract them from minutes
  // and add them to the hours
  if (minutes - 60 >= 0) {
    hours += 1;
    minutes -= 60;
  }

  const minutesString = minutes < 10 ? `0${minutes}` : String(minutes);

  return `${value < 0 ? '-' : ''}${hours}:${minutesString}`;
};

export const contractValueFormatter = (value, timeUnit) => {
  return timeUnit === 'hour' ? formatContractHours(value) : round(value, 2);
};

const formatWithMoment = (time, format) => {
  moment.locale(LOCALE);
  return time ? moment(time).format(format) : '';
};

// Renders the time only. If showMinuteIfZero is false, the minute part will be
// omitted. E.g. "7" instead of "7:00". This is used in FullCalendar month view
// where the space is very limited
export const formatTimeOnlyShort = (time, { showMinuteIfZero = true } = {}) => {
  if (!time) {
    return '';
  } else if (showMinuteIfZero || moment(time).minute() !== 0) {
    return formatWithMoment(time, TIME_ONLY_FORMAT_SHORT);
  } else {
    return formatWithMoment(time, 'H');
  }
};

export const formatTime = time => formatWithMoment(time, DATETIME_FORMAT);
export const formatTimeOnly = time => formatWithMoment(time, TIME_FORMAT);
export const formatTimeShort = time => formatWithMoment(time, TIME_FORMAT_SHORT);
export const formatDate = time => formatWithMoment(time, DATE_FORMAT);
export const formatWeekday = time => formatWithMoment(time, WEEKDAY_FORMAT);
export const formatWeekNumber = time => formatWithMoment(time, 'W');
export const formatTimeHuman = (time, options = { shorten: false }) =>
  formatWithMoment(time, options.shorten ? HUMANIZED_DATE_TIME_FORMAT_SHORT : HUMANIZED_DATE_TIME_FORMAT);

// today -> today
// saturday -> saturday
// more than 7 days in the future -> formatTimeHuman
export const formatTimeHumanRelative = time => {
  moment.locale(LOCALE);
  const now = moment();
  const timeMoment = moment(time);
  const timeLabel = timeMoment.format('H:mm');
  const diffInDays = timeMoment.startOf('day').diff(now.startOf('day'), 'days');

  if (diffInDays === 0) {
    return `${I18n.t('dates.today')} ${timeLabel}`;
  } else if (diffInDays === 1) {
    return `${I18n.t('dates.tomorrow')} ${timeLabel}`;
  } else if (diffInDays > 1 && diffInDays <= 7) {
    // capitalize the first letter of the day name, as moment returns it in
    // lowercase for some languages (e.g. Dutch)
    return `${capitalize(timeMoment.format('dddd'))} ${timeLabel}`;
  } else {
    return formatTimeHuman(time);
  }
};

export const FormattedPeriod = props => {
  const startTime = moment(props.start);
  const endTime = moment(props.end);
  // Prop to decide how the current date appear
  // by defult is set to true
  const showNowLabel = props.showNowLabel == undefined ? true : props.showNowLabel;
  const current = moment().isBetween(moment(startTime), moment(endTime));
  const dayChange = startTime.dayOfYear() !== endTime.dayOfYear();
  const dateStringFormat = props.detailed ? HUMANIZED_DATE_TIME_FORMAT : TIME_FORMAT_SHORT;

  // strangely, we need to set LOCALE here again, or it defaults to 'en'
  moment.locale(LOCALE);

  // Depending of the prop we select to show the label or the text
  const label = showNowLabel ? <CurrentLabel /> : I18n.t('now');
  return (
    <>
      {current && showNowLabel ? label : startTime.format(dateStringFormat)}
      {props.end && `- ${dayChange ? endTime.format(dateStringFormat) : endTime.format('HH:mm')}`}
    </>
  );
};

export const FormattedDates = props => {
  const startTime = moment(props.start);
  const endTime = moment(props.end);
  moment.locale(LOCALE);
  if (
    startTime.date() === endTime.date() &&
    startTime.month() === endTime.month() &&
    startTime.year() === endTime.year()
  ) {
    return `${startTime.format('ddd DD MMM HH:mm')} - ${endTime.format('HH:mm')}`;
  }
  return (
    <abbr title={`${startTime.format('ddd DD MMM HH:mm')} - ${endTime.format('ddd DD MMM HH:mm')}`}>
      {startTime.format('ddd DD-MM-YYYY')}
    </abbr>
  );
};

export const startOfMinute = time => moment(time).startOf('minute').seconds(0);

const QUARTER = 15 * 60 * 1000;
export const roundTimeDown = (dateTime = moment(), granularity = QUARTER) => {
  return moment(Math.floor(dateTime / granularity) * granularity);
};

// Function receives an integer and trasnforms it into time with format MM:SS
export const secondsToHumanTime = seconds => {
  const mins = Math.floor(seconds / 60);
  const sec = seconds % 60;
  const time = `${mins}:${sec < 10 ? '0' : ''}${sec}`;
  return time;
};

// Returns true if an event falls on a single day. If the end of the event is
// exactly at the start of the next day, e.g. 19:00 - 0:00, this is also
// considered a same day event
export const isMultiDayEvent = (start, end) => {
  start = moment(start);
  end = moment(end);

  // if the end time is exactly at the start of the day, consider it part of the
  // previous day
  if (end.hour() === 0 && end.minute() === 0 && end.second() === 0) {
    end = end.clone().subtract(1);
  }

  return !start.startOf('day').isSame(end.startOf('day'));
};

export const isFullDayInterval = interval => {
  if (!interval) return false;

  const start = moment(interval.start_time);
  const end = moment(interval.end_time);
  return start.hour() === 0 && start.minute() === 0 && moment(start).add(1, 'day').isSame(end);
};
